import * as React from "react";

import { cn } from "@/lib/utils";

// 自作アバター（radix 非依存）。画像の読み込み状態を context で共有し、
// 読み込み前・失敗時は Fallback を表示する。
type Status = "idle" | "loaded" | "error";
const AvatarCtx = React.createContext<{
  status: Status;
  setStatus: (s: Status) => void;
} | null>(null);

function Avatar({
  className,
  size = "default",
  ...props
}: React.ComponentProps<"span"> & { size?: "default" | "sm" | "lg" }) {
  const [status, setStatus] = React.useState<Status>("idle");
  const ctx = React.useMemo(() => ({ status, setStatus }), [status]);
  const sizeCls =
    size === "sm" ? "size-6 text-xs" : size === "lg" ? "size-10" : "size-8";
  return (
    <AvatarCtx.Provider value={ctx}>
      <span
        data-slot="avatar"
        data-size={size}
        className={cn(
          "relative inline-flex shrink-0 select-none overflow-hidden rounded-full bg-muted",
          sizeCls,
          className
        )}
        {...props}
      />
    </AvatarCtx.Provider>
  );
}

function AvatarImage({
  className,
  onLoad,
  onError,
  // Google のプロフィール画像（lh3.googleusercontent.com）は Referer 付きだと
  // 403 を返すことがあるため、既定で referrer を送らない
  referrerPolicy = "no-referrer",
  ...props
}: React.ComponentProps<"img">) {
  const ctx = React.useContext(AvatarCtx);
  const status = ctx?.status ?? "idle";
  const ref = React.useRef<HTMLImageElement>(null);

  // キャッシュ済みの画像はマウント前に読み込みが完了し onLoad が発火しないことがある。
  // その場合フォールバックのまま固まるので、実際の読み込み状態を直接見て補正する。
  React.useEffect(() => {
    const img = ref.current;
    if (!img || !img.complete) return;
    ctx?.setStatus(img.naturalWidth > 0 ? "loaded" : "error");
  }, [ctx, props.src]);

  return (
    <img
      ref={ref}
      data-slot="avatar-image"
      referrerPolicy={referrerPolicy}
      className={cn(
        "aspect-square size-full rounded-full object-cover",
        status === "loaded" ? "block" : "hidden",
        className
      )}
      onLoad={(e) => {
        ctx?.setStatus("loaded");
        onLoad?.(e);
      }}
      onError={(e) => {
        ctx?.setStatus("error");
        onError?.(e);
      }}
      {...props}
    />
  );
}

function AvatarFallback({ className, ...props }: React.ComponentProps<"span">) {
  const ctx = React.useContext(AvatarCtx);
  if (ctx?.status === "loaded") return null;
  return (
    <span
      data-slot="avatar-fallback"
      className={cn(
        "flex size-full items-center justify-center rounded-full bg-muted text-sm font-medium text-muted-foreground",
        className
      )}
      {...props}
    />
  );
}

// 以下は互換のためのエクスポート（現状は最小実装）。
function AvatarGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="avatar-group"
      className={cn(
        "flex -space-x-2 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-background",
        className
      )}
      {...props}
    />
  );
}

function AvatarGroupCount({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="avatar-group-count"
      className={cn(
        "relative flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm text-muted-foreground ring-2 ring-background",
        className
      )}
      {...props}
    />
  );
}

function AvatarBadge({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="avatar-badge"
      className={cn(
        "absolute bottom-0 right-0 z-10 inline-flex size-2.5 items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-background",
        className
      )}
      {...props}
    />
  );
}

export {
  Avatar,
  AvatarImage,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarBadge,
};
